import React, { useState, useEffect } from "react";
import useAxios from "../../utils/useAxios";
import { Link } from "react-router-dom";
import Select from "react-select";
import Pagination from "../common/pagination";

const Recipes = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [masterIngredients, setMasterIngredients] = useState([]);
  
  // UI States
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  // Filter States
  const [filterDifficulty, setFilterDifficulty] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterFeatured, setFilterFeatured] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination State
  const [page, setPage] = useState(1);
  const itemsPerPage = 50;

  // Form State
  const initialFormState = {
    name: "",
    description: "",
    image: "",
    video_id: "",
    sub_category_id: "",
    category_ids: [],
    cooking_time: "",
    prep_time: "",
    difficulty: "medium",
    default_servings: 1,
    is_featured: "NO",
    instructions: [""],
    ingredients: [{ ingredient_id: "", quantity: "", unit: "" }]
  };
  const [formData, setFormData] = useState(initialFormState);

  const axiosAPI = useAxios();

  const fetchRecipes = async () => {
    try {
      const response = await axiosAPI.get(`/api/recipe-management/recipes`);
      if (response.data.success) {
        setRecipes(response.data.recipes);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching recipes:", error);
      setLoading(false);
    }
  };

  const fetchMasterIngredients = async () => {
    try {
      const response = await axiosAPI.get(`/api/recipe-management/ingredients`);
      if (response.data.success) {
        setMasterIngredients(response.data.ingredients);
      }
    } catch (error) {
      console.error("Error fetching ingredients:", error);
    }
  };

  const [subCategory, setSubCategory] = useState([]);
  const [categories, setCategories] = useState([]);

  const fetchSubCategory = async () => {
    try {
      const response = await axiosAPI.get(`/api/v1/all-subcategories`);
      setSubCategory(response?.data?.subcategories || []);
    } catch (error) {
      console.error("Error fetching category:", error);
      setSubCategory([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosAPI.get(`/api/v1/get-categories?limit=100`);
      setCategories(response?.data?.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchRecipes();
    fetchMasterIngredients();
    fetchSubCategory();
    fetchCategories();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Instruction Handlers
  const handleInstructionChange = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = value;
    setFormData({ ...formData, instructions: newInstructions });
  };
  const addInstruction = () => {
    setFormData({ ...formData, instructions: [...formData.instructions, ""] });
  };
  const removeInstruction = (index) => {
    const newInstructions = formData.instructions.filter((_, i) => i !== index);
    setFormData({ ...formData, instructions: newInstructions });
  };

  // Ingredient Handlers
  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index][field] = value;
    setFormData({ ...formData, ingredients: newIngredients });
  };
  const addIngredient = () => {
    setFormData({ ...formData, ingredients: [...formData.ingredients, { ingredient_id: "", quantity: "", unit: "" }] });
  };
  const removeIngredient = (index) => {
    if (formData.ingredients.length === 1) return; // Need at least 1
    const newIngredients = formData.ingredients.filter((_, i) => i !== index);
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const handleEdit = async (id) => {
    try {
      const response = await axiosAPI.get(`/api/recipe-management/recipes/${id}`);
      if (response.data.success) {
        const recipe = response.data.recipe;
        let inst = recipe.instructions;
        if (typeof inst === 'string') {
          try { inst = JSON.parse(inst); } catch (e) { inst = [inst]; }
        }
        if (!Array.isArray(inst)) inst = [inst];

        let catIds = [];
        if (recipe.category_ids) {
          if (Array.isArray(recipe.category_ids)) {
            catIds = recipe.category_ids;
          } else if (typeof recipe.category_ids === 'string') {
            catIds = recipe.category_ids
              .split(',')
              .map((id) => Number(id.trim()) || id.trim())
              .filter(Boolean);
          }
        }

        setFormData({
          name: recipe.name || "",
          description: recipe.description || "",
          image: recipe.image || "",
          video_id: recipe.video_id || "",
          sub_category_id: recipe.sub_category_id || "",
          category_ids: catIds,
          cooking_time: recipe.cooking_time || "",
          prep_time: recipe.prep_time || "",
          difficulty: recipe.difficulty || "medium",
          default_servings: recipe.default_servings || 1,
          is_featured: recipe.is_featured || "NO",
          instructions: inst,
          ingredients: response.data.ingredients || [{ ingredient_id: "", quantity: "", unit: "" }]
        });
        setEditId(id);
        setIsEditing(true);
        setShowForm(true);
      }
    } catch (error) {
      console.error("Error fetching recipe details:", error);
    }
  };

  const handleFeaturedToggle = async (id, newValue) => {
    try {
      setRecipes((prev) =>
        prev.map((r) => (r.id === id ? { ...r, is_featured: newValue } : r))
      );
      await axiosAPI.patch(`/api/recipe-management/recipes/${id}/featured`, {
        is_featured: newValue,
      });
    } catch (error) {
      console.error("Error updating featured status:", error);
      alert("Failed to update is_featured status");
      fetchRecipes();
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this recipe?")) {
      try {
        const response = await axiosAPI.delete(`/api/recipe-management/recipes/${id}`);
        if (response.data.success) {
          alert("Deleted successfully");
          fetchRecipes();
        }
      } catch (error) {
        console.error("Error deleting recipe:", error);
        alert("Failed to delete recipe");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (formData.instructions.length === 0 || !formData.instructions[0]) {
      alert("At least one instruction step is required.");
      return;
    }
    const validIngredients = formData.ingredients.filter(i => i.ingredient_id && i.quantity);
    if (validIngredients.length === 0) {
      alert("At least one valid ingredient with quantity is required.");
      return;
    }

    const payload = { ...formData, ingredients: validIngredients };

    try {
      let response;
      if (isEditing) {
        response = await axiosAPI.put(`/api/recipe-management/recipes/${editId}`, payload);
      } else {
        response = await axiosAPI.post(`/api/recipe-management/recipes`, payload);
      }

      if (response.data.success) {
        alert(isEditing ? "Recipe updated successfully!" : "Recipe created successfully!");
        setShowForm(false);
        setIsEditing(false);
        setEditId(null);
        setFormData(initialFormState);
        fetchRecipes();
      }
    } catch (error) {
      console.error("Error saving recipe:", error);
      alert("Failed to save recipe. Please check your inputs.");
    }
  };

  const filteredRecipes = recipes.filter((recipe) => {
    // 1. Difficulty filter
    if (filterDifficulty !== "all") {
      if ((recipe.difficulty || "").toLowerCase() !== filterDifficulty.toLowerCase()) {
        return false;
      }
    }

    // 2. Category filter
    if (filterCategory !== "all") {
      const targetCatId = String(filterCategory);
      let matchesCategory = false;

      // Check recipe direct category_ids
      if (recipe.category_ids) {
        const catIds = String(recipe.category_ids).split(",").map((id) => id.trim());
        if (catIds.includes(targetCatId)) {
          matchesCategory = true;
        }
      }

      // Check subcategory's category_ids
      if (!matchesCategory && recipe.sub_category_id) {
        const sub = subCategory.find((s) => String(s.id) === String(recipe.sub_category_id));
        if (sub && sub.category_ids) {
          const subCatIds = String(sub.category_ids).split(",").map((id) => id.trim());
          if (subCatIds.includes(targetCatId)) {
            matchesCategory = true;
          }
        }
      }

      if (!matchesCategory) return false;
    }

    // 3. Is Featured filter
    if (filterFeatured !== "all") {
      const recFeatured = (recipe.is_featured || "NO").toUpperCase();
      if (recFeatured !== filterFeatured.toUpperCase()) {
        return false;
      }
    }

    // 4. Search term filter
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      if (!recipe.name?.toLowerCase().includes(term)) {
        return false;
      }
    }

    return true;
  });

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [filterDifficulty, filterCategory, filterFeatured, searchTerm]);

  // Pagination Calculations
  const totalPages = Math.ceil(filteredRecipes.length / itemsPerPage) || 1;
  const paginatedRecipes = filteredRecipes.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  return (
    <div className="container">
      <div className="page-inner">
        <div className="page-header d-flex justify-content-between align-items-center">
          <h3 className="fw-bold mb-3">Recipes</h3>
          {!showForm && (
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>
              <i className="fas fa-plus"></i> Add New Recipe
            </button>
          )}
        </div>

        {showForm ? (
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title">{isEditing ? "Edit Recipe" : "Add Recipe"}</h4>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6 form-group">
                        <label>Recipe Name <span className="text-danger">*</span></label>
                        <input type="text" className="form-control" name="name" value={formData.name} onChange={handleInputChange} required />
                      </div>
                      <div className="col-md-6 form-group">
                        <label>Video ID</label>
                        <input type="text" className="form-control" name="video_id" value={formData.video_id} onChange={handleInputChange} />
                        {formData.video_id && (
                          <div className="mt-2">
                            <label className="text-muted small mb-1">Thumbnail Preview</label>
                            <br />
                            <img 
                              src={`https://img.youtube.com/vi/${formData.video_id}/hqdefault.jpg`} 
                              alt="Thumbnail Preview" 
                              className="img-thumbnail shadow-sm" 
                              style={{ maxWidth: '200px', borderRadius: '8px', objectFit: 'cover' }} 
                            />
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 form-group">
                        <label>Thumbnail / Image URL</label>
                        <input type="text" className="form-control" name="image" value={formData.image} onChange={handleInputChange} placeholder="Enter image URL" />
                        {formData.image && (
                          <div className="mt-2">
                            <label className="text-muted small mb-1">Image Preview</label>
                            <br />
                            <img 
                              src={formData.image} 
                              alt="Thumbnail Preview" 
                              className="img-thumbnail shadow-sm" 
                              style={{ maxWidth: '200px', borderRadius: '8px', objectFit: 'cover' }} 
                            />
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 form-group">
                        <label>Sub Category</label>
                        <Select
                          options={subCategory?.map((sub) => ({
                            value: sub.id,
                            label: sub.name,
                          }))}
                          value={
                            formData.sub_category_id
                              ? {
                                  value: formData.sub_category_id,
                                  label: subCategory.find(
                                    (sub) => String(sub.id) === String(formData.sub_category_id)
                                  )?.name || "Select...",
                                }
                              : null
                          }
                          onChange={(selectedOption) => {
                            const subId = selectedOption ? selectedOption.value : "";
                            const foundSub = subCategory.find(
                              (sub) => String(sub.id) === String(subId)
                            );
                            let newCatIds = formData.category_ids;
                            // If no categories currently selected, auto-suggest from subcategory
                            if ((!newCatIds || newCatIds.length === 0) && foundSub?.category_ids) {
                              newCatIds = String(foundSub.category_ids)
                                .split(",")
                                .map((id) => Number(id.trim()) || id.trim())
                                .filter(Boolean);
                            }
                            setFormData((prev) => ({
                              ...prev,
                              sub_category_id: subId,
                              category_ids: newCatIds,
                            }));
                          }}
                          isClearable
                          placeholder="Search and Select Sub Category..."
                        />
                      </div>
                      <div className="col-md-6 form-group">
                        <label>Category</label>
                        <Select
                          isMulti
                          options={categories?.map((cat) => ({
                            value: cat.id,
                            label: cat.cat_name,
                          }))}
                          value={
                            categories
                              ?.filter((cat) =>
                                (formData.category_ids || [])
                                  .map((id) => String(id))
                                  .includes(String(cat.id))
                              )
                              .map((cat) => ({
                                value: cat.id,
                                label: cat.cat_name,
                              }))
                          }
                          onChange={(selectedOptions) => {
                            const newIds =
                              selectedOptions?.map((opt) => opt.value) || [];
                            setFormData((prev) => ({
                              ...prev,
                              category_ids: newIds,
                            }));
                          }}
                          placeholder="Search and Select Categories..."
                          isClearable
                        />
                      </div>
                      <div className="col-md-12 form-group">
                        <label>Description</label>
                        <textarea className="form-control" name="description" value={formData.description} onChange={handleInputChange} rows="2"></textarea>
                      </div>
                      <div className="col-md-2 form-group">
                        <label>Prep Time (mins)</label>
                        <input type="number" className="form-control" name="prep_time" value={formData.prep_time} onChange={handleInputChange} />
                      </div>
                      <div className="col-md-2 form-group">
                        <label>Cooking Time (mins)</label>
                        <input type="number" className="form-control" name="cooking_time" value={formData.cooking_time} onChange={handleInputChange} />
                      </div>
                      <div className="col-md-3 form-group">
                        <label>Difficulty</label>
                        <select className="form-control" name="difficulty" value={formData.difficulty} onChange={handleInputChange}>
                          <option value="easy">Easy</option>
                          <option value="medium">Medium</option>
                          <option value="hard">Hard</option>
                        </select>
                      </div>
                      <div className="col-md-2 form-group">
                        <label>Servings (default)</label>
                        <input type="number" className="form-control" name="default_servings" value={formData.default_servings} onChange={handleInputChange} />
                      </div>
                      <div className="col-md-3 form-group">
                        <label>Is Featured</label>
                        <select className="form-control" name="is_featured" value={formData.is_featured || "NO"} onChange={handleInputChange}>
                          <option value="NO">NO</option>
                          <option value="YES">YES</option>
                        </select>
                      </div>
                    </div>

                    <hr />
                    
                    <h5>Instructions <span className="text-danger">*</span></h5>
                    {formData.instructions.map((inst, index) => (
                      <div key={index} className="d-flex mb-2">
                        <span className="p-2">{index + 1}.</span>
                        <input type="text" className="form-control me-2" value={inst} onChange={(e) => handleInstructionChange(index, e.target.value)} required />
                        <button type="button" className="btn btn-danger btn-sm" onClick={() => removeInstruction(index)}><i className="fas fa-trash"></i></button>
                      </div>
                    ))}
                    <button type="button" className="btn btn-sm btn-secondary mb-3" onClick={addInstruction}>+ Add Step</button>

                    <hr />

                    <h5>Ingredients <span className="text-danger">*</span></h5>
                    {formData.ingredients.map((ing, index) => (
                      <div key={index} className="row mb-2">
                        <div className="col-md-5">
                          <select className="form-control" value={ing.ingredient_id} onChange={(e) => handleIngredientChange(index, 'ingredient_id', e.target.value)} required>
                            <option value="">Select Ingredient</option>
                            {masterIngredients.map(mi => (
                              <option key={mi.id} value={mi.id}>{mi.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-3">
                          <input type="number" className="form-control" placeholder="Quantity" value={ing.quantity} onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)} required />
                        </div>
                        <div className="col-md-3">
                          <input type="text" className="form-control" placeholder="Unit (e.g., g, cups)" value={ing.unit} onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)} required />
                        </div>
                        <div className="col-md-1 text-center">
                          <button type="button" className="btn btn-danger btn-sm mt-1" onClick={() => removeIngredient(index)} disabled={formData.ingredients.length === 1}><i className="fas fa-trash"></i></button>
                        </div>
                      </div>
                    ))}
                    <button type="button" className="btn btn-sm btn-secondary mb-3" onClick={addIngredient}>+ Add Ingredient</button>

                    <hr />
                    <div className="form-group text-end">
                      <button type="button" className="btn btn-warning me-2" onClick={() => { setShowForm(false); setIsEditing(false); setFormData(initialFormState); }}>Cancel</button>
                      <button type="submit" className="btn btn-success">Save Recipe</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header">
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <h4 className="card-title mb-0">All Recipes</h4>
                      <span className="badge bg-secondary">
                        {filteredRecipes.length} of {recipes.length}
                      </span>
                    </div>

                    {/* Filter Controls */}
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {/* Search Filter */}
                      <div style={{ minWidth: "160px" }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Search name..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>

                      {/* Category Filter */}
                      <div style={{ minWidth: "180px" }}>
                        <select
                          className="form-select form-control form-control-sm"
                          value={filterCategory}
                          onChange={(e) => setFilterCategory(e.target.value)}
                        >
                          <option value="all">All Categories</option>
                          {categories
                            ?.slice()
                            .sort((a, b) => a.cat_name.localeCompare(b.cat_name))
                            .map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.cat_name}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* Difficulty Filter */}
                      <div style={{ minWidth: "140px" }}>
                        <select
                          className="form-select form-control form-control-sm"
                          value={filterDifficulty}
                          onChange={(e) => setFilterDifficulty(e.target.value)}
                        >
                          <option value="all">All Difficulties</option>
                          <option value="easy">Easy</option>
                          <option value="medium">Medium</option>
                          <option value="hard">Hard</option>
                        </select>
                      </div>

                      {/* Featured Filter */}
                      <div style={{ minWidth: "130px" }}>
                        <select
                          className="form-select form-control form-control-sm"
                          value={filterFeatured}
                          onChange={(e) => setFilterFeatured(e.target.value)}
                        >
                          <option value="all">All Featured</option>
                          <option value="YES">Featured (YES)</option>
                          <option value="NO">Not Featured (NO)</option>
                        </select>
                      </div>

                      {/* Reset Button */}
                      {(filterCategory !== "all" || filterDifficulty !== "all" || filterFeatured !== "all" || searchTerm !== "") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            setFilterCategory("all");
                            setFilterDifficulty("all");
                            setFilterFeatured("all");
                            setSearchTerm("");
                            setPage(1);
                          }}
                          title="Reset Filters"
                        >
                          <i className="fas fa-undo"></i> Reset
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="table-responsive">
                    <table className="display table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Category</th>
                          <th>Difficulty</th>
                          <th>Prep Time</th>
                          <th>Cook Time</th>
                          <th>Servings</th>
                          <th>IS FEATURED</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr><td colSpan="9" className="text-center py-4">Loading...</td></tr>
                        ) : paginatedRecipes.length > 0 ? (
                          paginatedRecipes.map((recipe) => (
                            <tr key={recipe.id}>
                              <td>{recipe.id}</td>
                              <td className="fw-semibold">{recipe.name}</td>
                              <td>
                                {recipe.category_ids
                                  ? String(recipe.category_ids)
                                      .split(",")
                                      .map((cid) => categories.find((c) => String(c.id) === String(cid.trim()))?.cat_name)
                                      .filter(Boolean)
                                      .join(", ") || "-"
                                  : "-"}
                              </td>
                              <td>
                                <span className={`badge ${
                                  (recipe.difficulty || "").toLowerCase() === "easy"
                                    ? "bg-success"
                                    : (recipe.difficulty || "").toLowerCase() === "hard"
                                    ? "bg-danger"
                                    : "bg-warning text-dark"
                                }`}>
                                  {recipe.difficulty ? recipe.difficulty.toUpperCase() : "MEDIUM"}
                                </span>
                              </td>
                              <td>{recipe.prep_time} mins</td>
                              <td>{recipe.cooking_time} mins</td>
                              <td>{recipe.default_servings}</td>
                              <td>
                                <select
                                  className={`form-select form-select-sm fw-bold ${
                                    (recipe.is_featured || "NO").toUpperCase() === "YES"
                                      ? "text-success border-success"
                                      : "text-muted"
                                  }`}
                                  style={{
                                    width: "85px",
                                    padding: "3px 8px",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    borderRadius: "6px"
                                  }}
                                  value={(recipe.is_featured || "NO").toUpperCase()}
                                  onChange={(e) => handleFeaturedToggle(recipe.id, e.target.value)}
                                >
                                  <option value="NO">NO</option>
                                  <option value="YES">YES</option>
                                </select>
                              </td>
                              <td>
                                <button className="btn btn-sm btn-primary me-2" onClick={() => handleEdit(recipe.id)}>Edit</button>
                                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(recipe.id)}>Delete</button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center py-4 text-muted">
                              No recipes found matching the selected filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Section */}
                  {filteredRecipes.length > 0 && (
                    <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 pt-3 border-top">
                      <div className="text-muted small">
                        Showing {(page - 1) * itemsPerPage + 1} to {Math.min(page * itemsPerPage, filteredRecipes.length)} of {filteredRecipes.length} recipes
                      </div>
                      <Pagination totalPages={totalPages} setPage={setPage} page={page} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Recipes;
